// apps/web/src/features/cargo/components/CargoImportResults.tsx
//
// Transient per-file import result lists (#1232/#1229): created / updated /
// warnings / errors. These are EPHEMERAL UI feedback from the import response —
// never re-read as persisted counters.

import { useTranslation } from 'react-i18next';
import type { CargoImportFileResult, CargoImportRowResult } from '@magimp/shared';

type Bucket = 'created' | 'updated' | 'warnings' | 'errors';

const BUCKET_TONE: Record<Bucket, string> = {
  created: 'var(--status-success-fg)',
  updated: 'var(--fg-2)',
  warnings: 'var(--status-warn-fg)',
  errors: 'var(--status-error-fg)',
};

function rowLabel(row: CargoImportRowResult): string {
  const article = row.article ?? '—';
  return row.message ? `${article} — ${row.message}` : article;
}

export function CargoImportResults({ files }: { files: readonly CargoImportFileResult[] }) {
  const { t } = useTranslation(['cargo']);
  const buckets: Bucket[] = ['created', 'updated', 'warnings', 'errors'];

  return (
    <div
      data-testid="cargo-import-results"
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      {files.map((file, fileIndex) => (
        <div
          key={`${file.objectKey}-${fileIndex}`}
          style={{
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--r-2)',
            padding: 10,
            background: 'var(--bg-1)',
          }}
        >
          <div style={{ fontSize: 12, color: 'var(--fg-2)', marginBottom: 6 }}>
            {t('cargo:import.fileLabel', { index: fileIndex + 1 })}
          </div>
          {buckets.map((bucket) => (
            <BucketList key={bucket} bucket={bucket} rows={file[bucket]} />
          ))}
        </div>
      ))}
    </div>
  );
}

function BucketList({ bucket, rows }: { bucket: Bucket; rows: readonly CargoImportRowResult[] }) {
  const { t } = useTranslation(['cargo']);
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: BUCKET_TONE[bucket] }}>
        {t(`cargo:import.buckets.${bucket}`)} ({rows.length})
      </div>
      {rows.length > 0 && (
        <ul style={{ margin: '2px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--fg-2)' }}>
          {rows.map((row, i) => (
            <li key={i}>{rowLabel(row)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
